import { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { api, errDetail, fmtIST } from "@/lib/api";
import { useUrlState } from "@/hooks/useUrlState";
import { useIncludeTest, withIncludeTest } from "@/hooks/useIncludeTest";
import { IncludeTestToggle } from "@/components/IncludeTestToggle";
import { buildReturnTo } from "@/hooks/useBackNavigation";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TestBadge, EmptyState } from "@/components/common";
import { OnboardingWizardDialog } from "@/components/OnboardingWizard";
import { HistoricalMigrationDialog } from "@/components/HistoricalMigrationDialog";
import { CaseBlockedPanel, isExceptionable, exceptionPayload, exceptionReady } from "@/components/CaseBlockedPanel";
import { ProcessOwnerPicker } from "@/components/ProcessOwnerPicker";
import { WorkflowSetupBanner } from "@/components/ExistingCaseWorkflowSetup";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import { Briefcase, Plus, X, ChevronLeft, ChevronRight, ArrowDownUp, SlidersHorizontal } from "lucide-react";

// The main queue set (2026-09 overview redesign). Every key is one of the
// server's own CASE_QUEUES, so the list and its count are always the same
// server-side query. Waiting is ONE queue here — its reason is a secondary
// control scoped to that queue only (WAITING_TABS below).
const QUEUES = [
  { key: "my", label: "My Cases" },
  { key: "team", label: "Team" },
  { key: "unassigned", label: "Unassigned" },
  { key: "due_today", label: "Due Today" },
  { key: "overdue", label: "Overdue" },
  { key: "waiting", label: "Waiting" },
  { key: "at_risk", label: "At Risk" },
  { key: "completed", label: "Completed" },
];

// Summary cards. Each one selects the queue whose server-side filter
// produced its number (Active == every active Case == the Team queue).
const SUMMARY_CARDS = [
  { key: "active", queue: "team", label: "Active" },
  { key: "overdue", queue: "overdue", label: "Overdue", tone: "danger" },
  { key: "due_today", queue: "due_today", label: "Due Today", tone: "warning" },
  // Round 7 Workstream E - labelled as CASES: unassigned leads and unassigned
  // tasks are different numbers (Leads page / Approvals -> Unassigned).
  { key: "unassigned", queue: "unassigned", label: "Unassigned Cases", tone: "warning" },
  { key: "at_risk", queue: "at_risk", label: "At Risk", tone: "danger" },
  { key: "waiting", queue: "waiting", label: "Waiting" },
  { key: "completed", queue: "completed", label: "Completed" },
];

// Secondary control, shown only while the Waiting queue is selected. Values
// are the server's own WAITING_REASONS, sent as `waiting_reason=`.
const WAITING_TABS = [
  { key: "", label: "All" },
  { key: "customer", label: "Customer" },
  { key: "documents", label: "Documents" },
  { key: "payment", label: "Payment" },
  { key: "external_party", label: "External" },
];

// Pre-redesign ?queue=waiting_<reason> links (the four old top-level tabs)
// still resolve: they are rewritten to the Waiting queue + that reason.
const LEGACY_WAITING_QUEUES = {
  waiting_customer: "customer", waiting_documents: "documents",
  waiting_payment: "payment", waiting_external_party: "external_party",
};
// URL keys of controls that no longer exist on this page; dropped from any
// old bookmarked URL so they can never silently narrow the list.
const REMOVED_URL_KEYS = ["q", "stage_age_min", "stage_age_max", "due_from", "due_to"];

const STATUS_CLS = {
  active: "bg-[hsl(var(--info))]/10 text-[hsl(var(--info))] border-transparent",
  waiting_customer: "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-transparent",
  at_risk: "bg-[hsl(var(--danger))]/15 text-[hsl(var(--danger))] border-transparent",
  completed: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-transparent",
  discontinued: "bg-muted text-muted-foreground border-transparent",
};

const WAITING_LABEL = {
  customer: "Customer", documents: "Documents",
  payment: "Payment", external_party: "External",
};

const SORTS = [
  ["created_at", "Newest"], ["due_at", "Due date"], ["stage_age", "Longest in stage"],
  ["priority", "Priority"], ["customer", "Customer"],
];

// One status pill. The canonical waiting status is "waiting_customer" with
// the WHY in `waiting_reason` — shown as "Waiting · Documents", never the
// misleading "waiting customer" + "Documents" pair.
const StatusPill = ({ c }) => {
  const label = c.status === "waiting_customer"
    ? `Waiting · ${WAITING_LABEL[c.waiting_reason] || WAITING_LABEL.customer}`
    : String(c.status || "").replace(/_/g, " ");
  return (
    <Badge variant="outline" data-testid={`case-row-status-${c.case_id}`}
      className={cn("whitespace-nowrap capitalize font-medium", STATUS_CLS[c.status])}>
      {label}
    </Badge>
  );
};

// Due date + the one canonical, server-derived Next Action for the Case.
const DueNextAction = ({ c, compact = false }) => (
  <div className="min-w-0" data-testid={`case-row-due-${c.case_id}`}>
    <div className={cn("text-xs tabular-nums",
      c.is_overdue ? "text-[hsl(var(--danger))] font-medium" : "text-foreground")}>
      {c.due_at ? fmtIST(c.due_at) : <span className="text-muted-foreground">No due date</span>}
      {c.is_overdue && <span className="ml-1.5 text-[10px] uppercase tracking-wide">Overdue</span>}
    </div>
    {c.next_action?.title && (
      <div className={cn("text-[11px] text-muted-foreground truncate", compact ? "max-w-full" : "max-w-[240px]")}
        title={c.next_action.title} data-testid={`case-row-next-action-${c.case_id}`}>
        {c.next_action.title}
      </div>
    )}
  </div>
);

const PriorityBadge = ({ priority }) => (priority && priority !== "normal" ? (
  <Badge variant="outline" className={cn("text-[10px] px-1.5 py-0 capitalize",
    priority === "high" && "border-[hsl(var(--danger))]/40 text-[hsl(var(--danger))]")}>
    {priority}
  </Badge>
) : null);

// Compact filter select — same size/shape for every toolbar control.
const FilterSelect = ({ value, onChange, testId, allLabel, children, className }) => (
  <Select value={value || "all"} onValueChange={(v) => onChange(v === "all" ? "" : v)}>
    <SelectTrigger data-testid={testId}
      className={cn("h-8 text-xs w-full sm:w-auto sm:min-w-[140px] bg-background",
        value && "border-primary/50 text-foreground font-medium", className)}>
      <SelectValue />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value="all">{allLabel}</SelectItem>
      {children}
    </SelectContent>
  </Select>
);

// `includeTest` is the Process page's own Include TEST switch: with it off TEST customers stay hidden from this
// search; with it on they are found, clearly labelled TEST, and can only ever produce a TEST Case.
export const NewCaseDialog = ({ open, setOpen, onCreated, includeTest = false }) => {
  const navigate = useNavigate();
  const [mode, setMode] = useState("find");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [contact, setContact] = useState(null);
  const [workflows, setWorkflows] = useState({});
  const [product, setProduct] = useState("");
  const [opportunityId, setOpportunityId] = useState(null);
  const [autoFilled, setAutoFilled] = useState(false);
  const [opportunities, setOpportunities] = useState([]);
  // R13 Workstream 4: the backend decides eligibility; the dialog only renders its answer.
  const [elig, setElig] = useState(null);
  const [useException, setUseException] = useState(false);
  const [exceptionReason, setExceptionReason] = useState("");
  const [evidenceRef, setEvidenceRef] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // One id per dialog opening: a double click or retry returns the first request's Case.
  const [requestId, setRequestId] = useState("");
  useEffect(() => {
    if (open) {
      api.get("/cases/workflows").then((r) => setWorkflows(r.data));
      setRequestId((typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `req-${Date.now()}-${Math.random()}`);
    }
    if (!open) {
      setMode("find"); setContact(null); setQ(""); setProduct(""); setOpportunityId(null);
      setElig(null); setUseException(false); setExceptionReason(""); setEvidenceRef(""); setSubmitting(false);
    }
  }, [open]);
  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(() => api.get(withIncludeTest(`/search?q=${encodeURIComponent(q)}`, includeTest))
      .then((r) => setResults(r.data)), 250);
    return () => clearTimeout(t);
  }, [q, includeTest]);
  useEffect(() => {
    if (!contact) { setProduct(""); setOpportunityId(null); setAutoFilled(false); setOpportunities([]); setElig(null); return; }
    api.get(`/contacts/${contact.contact_id}/full`).then((r) => {
      const opps = (r.data?.opportunities || []).filter((o) => o.lifecycle_status === "active");
      setOpportunities(opps);
      const active = opps[0];
      if (active) { setProduct(active.product_code); setOpportunityId(active.opportunity_id); setAutoFilled(true); }
    }).catch(() => {});
  }, [contact]);
  // Pre-flight: refreshed whenever the customer / product / opportunity changes.
  useEffect(() => {
    if (!contact || !product) { setElig(null); return; }
    let cancelled = false;
    const qs = new URLSearchParams({ contact_id: contact.contact_id, product_code: product });
    if (opportunityId) qs.set("opportunity_id", opportunityId);
    api.get(`/cases/eligibility?${qs}`).then((r) => { if (!cancelled) setElig(r.data); })
      .catch(() => { if (!cancelled) setElig(null); });
    return () => { cancelled = true; };
  }, [contact, product, opportunityId]);
  const onProductChange = (v) => {
    setProduct(v); setAutoFilled(false);
    const match = opportunities.find((o) => o.product_code === v);
    setOpportunityId(match ? match.opportunity_id : null);
  };
  const blocked = !!elig && !elig.allowed;
  const exceptionable = isExceptionable(elig);
  const exceptionOk = exceptionReady(useException, exceptionReason, evidenceRef);
  const createDisabled = !contact || !product || submitting || (blocked && !(exceptionable && exceptionOk));
  const create = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = { contact_id: contact.contact_id, product_code: product,
        opportunity_id: opportunityId, request_id: requestId };
      if (blocked && useException) Object.assign(payload, exceptionPayload(elig, exceptionReason, evidenceRef));
      const { data } = await api.post("/cases", payload);
      if (data.created === false) {
        toast.info(data.message || `Case ${data.case_id} already exists - opened it instead of creating another.`,
          { duration: 7000 });
      } else {
        toast.success("Case created");
      }
      setOpen(false); setContact(null); setQ(""); onCreated();
      if (data.created === false && data.case_id) navigate(`/process/${data.case_id}`);
    } catch (e) {
      toast.error(errDetail(e), { duration: 9000 });
    } finally { setSubmitting(false); }
  };
  const onWizardCreated = () => {
    toast.info("Customer created — this opportunity isn't eligible for a Process case yet "
      + "(needs Signup, payment and approval first). Find it from Leads/Pipelines to continue.",
      { duration: 8000 });
    setOpen(false); onCreated();
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle className="font-heading">New process case</DialogTitle></DialogHeader>
        <div className="flex gap-2">
          <Button type="button" size="sm" variant={mode === "find" ? "default" : "outline"}
            data-testid="case-mode-find" onClick={() => setMode("find")}>Find existing customer</Button>
          <Button type="button" size="sm" variant={mode === "create" ? "default" : "outline"}
            data-testid="case-mode-create" onClick={() => setMode("create")}>Create new customer</Button>
        </div>
        {mode === "find" ? (
          <div className="space-y-3">
            {!contact ? (
              <div className="space-y-1.5">
                <Label className="text-xs">Find customer</Label>
                <Input data-testid="case-customer-search" placeholder="Name / phone / AP-…"
                  value={q} onChange={(e) => setQ(e.target.value)} />
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {results.map((r) => (
                    <button key={r.contact_id} data-testid={`case-customer-pick-${r.customer_id}`}
                      onClick={() => setContact(r)}
                      className="w-full text-left p-2 rounded border text-sm hover:bg-muted/50">
                      {r.full_name} <span className="font-mono text-xs text-muted-foreground">{r.customer_id}</span>
                      {r.is_test_record && <> <TestBadge show /></>}
                    </button>
                  ))}
                  {q.trim().length >= 2 && results.length === 0 && !includeTest && (
                    <p data-testid="case-search-test-hint" className="text-[11px] text-muted-foreground px-1">
                      No match. TEST customers are hidden - turn on Include TEST to find them.
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-1">
                <p className="text-sm">Customer: <b>{contact.full_name}</b>{" "}
                  {contact.is_test_record && <TestBadge show />}{" "}
                  <button className="text-xs underline text-muted-foreground" onClick={() => setContact(null)}>change</button></p>
                {contact.is_test_record && (
                  <p data-testid="case-test-customer-note" className="text-[11px] text-muted-foreground">
                    TEST customer: this can only create a TEST Case. It sends nothing to any provider and is
                    excluded from reports and the default Case list.
                  </p>
                )}
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs">Product</Label>
              <Select value={product} onValueChange={onProductChange}>
                <SelectTrigger data-testid="case-product-select" className="h-9"><SelectValue placeholder="Pick product" /></SelectTrigger>
                <SelectContent>{Object.keys(workflows).map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
              </Select>
              {product && <p className="text-[11px] text-muted-foreground">
                {autoFilled ? "Auto-filled from the customer's own record — " : ""}{(workflows[product] || []).join(" → ")}</p>}
            </div>
            {contact && opportunities.length > 1 && (
              <div className="space-y-1.5">
                <Label className="text-xs">Opportunity</Label>
                <Select value={opportunityId || ""} onValueChange={(v) => setOpportunityId(v)}>
                  <SelectTrigger data-testid="case-opportunity-select" className="h-9"><SelectValue placeholder="Pick opportunity" /></SelectTrigger>
                  <SelectContent>
                    {opportunities.map((o) => (
                      <SelectItem key={o.opportunity_id} value={o.opportunity_id}>
                        {o.product_code} — {o.current_stage_name || "no stage"}
                      </SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <CaseBlockedPanel elig={elig} useException={useException} setUseException={setUseException}
              exceptionReason={exceptionReason} setExceptionReason={setExceptionReason}
              evidenceRef={evidenceRef} setEvidenceRef={setEvidenceRef}
              onOpenCase={(id) => { setOpen(false); navigate(`/process/${id}`); }} />
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Opens the same dedup-checked onboarding wizard every other Create-customer entry point uses. A brand-new opportunity has no consultation outcome, signup or payment yet — it will NOT get an active case immediately.</p>
            <OnboardingWizardDialog onCreated={onWizardCreated} triggerLabel="Create customer" triggerTestId="case-nc-create-button" />
          </div>
        )}
        <DialogFooter>
          {mode === "find" && (
            <Button data-testid="case-create-button" disabled={createDisabled} onClick={create}>
              {submitting ? "Creating…" : "Create case"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function ProcessPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasCap } = useAuth();
  // Same capability pair the server's PATCH /cases/{id}/process-owner gate
  // uses — the picker is only offered to people it would accept.
  const canAssign = hasCap("process.cases.reassign") || hasCap("crm.assign");
  // ALL list state lives in the URL (shared Back-navigation contract:
  // `queue=`/`case=` keep their original keys and meaning), so "open a Case
  // from a filtered, paginated queue and hit Back" restores the exact list.
  // `waiting` is the Waiting queue's secondary reason tab — it is only ever
  // sent to the server while queue === "waiting".
  const [state, setState] = useUrlState({
    queue: "team", case: "", owner: "", product: "", stage: "",
    priority: "", waiting: "", sort: "created_at", direction: "desc",
    page: 1, page_size: 25,
  });
  const { queue, case: legacyCase, owner, product, stage, priority, waiting,
    sort, direction, page, page_size } = state;
  const legacyWaiting = LEGACY_WAITING_QUEUES[queue];
  const isWaiting = queue === "waiting";
  // Round 7 Workstream F - TEST Cases hidden unless an authorised admin turns
  // the visible toggle on (URL-scoped, never silently persisted).
  const { includeTest, canInclude, setIncludeTest } = useIncludeTest();

  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [newOpen, setNewOpen] = useState(false);
  const [migrationOpen, setMigrationOpen] = useState(new URLSearchParams(location.search).get("migration") === "1");
  const [users, setUsers] = useState([]);
  const [workflows, setWorkflows] = useState({});
  const [dash, setDash] = useState(null);
  const [waitingCounts, setWaitingCounts] = useState(null);
  const [showFilters, setShowFilters] = useState(false); // phone-width only

  // Legacy contract: ?case=CS-… used to open the old modal. Redirect it to
  // the real route, preserving the rest of the list URL as return_to so
  // Back still lands on the same filtered queue.
  useEffect(() => {
    if (!legacyCase) return;
    const rest = new URLSearchParams(location.search);
    rest.delete("case");
    const listUrl = `${location.pathname}${rest.toString() ? `?${rest}` : ""}`;
    navigate(`/process/${legacyCase}?return_to=${encodeURIComponent(listUrl)}`, { replace: true });
  }, [legacyCase, location.pathname, location.search, navigate]);

  // Old bookmarks: ?queue=waiting_<reason> → Waiting queue + that reason
  // tab; a stray ?waiting= outside the Waiting queue is dropped (the server
  // would otherwise narrow e.g. "Overdue" to waiting Cases only); and keys of
  // removed controls are cleared from the URL.
  const hasRemovedKeys = REMOVED_URL_KEYS.some((k) => new URLSearchParams(location.search).has(k));
  useEffect(() => {
    if (legacyCase) return;
    const patch = {};
    if (legacyWaiting) Object.assign(patch, { queue: "waiting", waiting: legacyWaiting });
    else if (!isWaiting && waiting) patch.waiting = "";
    if (hasRemovedKeys) REMOVED_URL_KEYS.forEach((k) => { patch[k] = ""; });
    if (Object.keys(patch).length) setState(patch);
  }, [legacyCase, legacyWaiting, isWaiting, waiting, hasRemovedKeys, setState]);

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data.users || r.data)).catch(() => {});
    api.get("/cases/workflows").then((r) => setWorkflows(r.data)).catch(() => {});
  }, []);

  // The shared filter set (everything except queue/page/sort) — used for the
  // list itself AND for the Waiting reason tab counts, so a count always
  // equals the row total you get by clicking that tab.
  const baseParams = useCallback(() => {
    const p = new URLSearchParams();
    if (owner) p.set("owner_user_id", owner);
    if (product) p.set("product_code", product);
    if (stage) p.set("stage", stage);
    if (priority) p.set("priority", priority);
    if (includeTest) p.set("include_test", "true");
    return p;
  }, [owner, product, stage, priority, includeTest]);

  const listParams = useCallback(() => {
    const p = baseParams();
    p.set("queue", queue);
    p.set("paginated", "true");
    p.set("page", String(page));
    p.set("page_size", String(page_size));
    p.set("sort", sort);
    p.set("direction", direction);
    p.set("include_next_action", "true");
    if (isWaiting && waiting) p.set("waiting_reason", waiting);
    return p;
  }, [baseParams, queue, page, page_size, sort, direction, isWaiting, waiting]);

  const fetchRows = useCallback(() => api.get(`/cases?${listParams().toString()}`)
    .then((r) => { setRows(r.data.rows); setTotal(r.data.total); setPages(r.data.pages); }),
  [listParams]);

  const load = useCallback(() => {
    setRows(null);
    fetchRows().catch((e) => { setRows([]); toast.error(errDetail(e, "Could not load cases")); });
    api.get(withIncludeTest("/cases/dashboard", includeTest)).then((r) => setDash(r.data)).catch(() => setDash(null));
  }, [fetchRows, includeTest]);

  useEffect(() => { if (!legacyCase && !legacyWaiting) load(); }, [load, legacyCase, legacyWaiting]);

  // Exact per-reason counts for the Waiting secondary tabs: the server's own
  // paginated `total` for queue=waiting (+ waiting_reason) under the same
  // filters as the list — never a client-side tally of one loaded page.
  useEffect(() => {
    if (!isWaiting) { setWaitingCounts(null); return undefined; }
    let cancelled = false;
    Promise.all(WAITING_TABS.map(({ key }) => {
      const p = baseParams();
      p.set("queue", "waiting");
      p.set("paginated", "true");
      p.set("page_size", "1");
      if (key) p.set("waiting_reason", key);
      return api.get(`/cases?${p.toString()}`).then((r) => [key, r.data.total]);
    })).then((pairs) => { if (!cancelled) setWaitingCounts(Object.fromEntries(pairs)); })
      .catch(() => { if (!cancelled) setWaitingCounts(null); });
    return () => { cancelled = true; };
  }, [isWaiting, baseParams]);

  // B1 — inline Process-owner assignment. Optimistic: the row shows the new
  // owner immediately; the real refetch then confirms it, or the row is
  // reverted if the server refused. Sales owner is never touched.
  const onOwnerAssigned = (caseId, prev) => (r) => {
    if (r.optimistic) {
      setRows((rs) => (rs || []).map((row) => (row.case_id === caseId
        ? { ...row, process_owner_user_id: r.process_owner_user_id,
            process_owner_name: r.process_owner_name } : row)));
      return;
    }
    if (r.revert) {
      setRows((rs) => (rs || []).map((row) => (row.case_id === caseId ? { ...row, ...prev } : row)));
      return;
    }
    fetchRows().catch(() => {});
  };

  // Changing any filter resets to page 1 — never leaves the user stranded
  // on a page number that no longer exists in the new result set.
  const setFilter = (patch) => setState({ ...patch, page: 1 });
  // Switching queue always clears the Waiting reason (it is scoped to the
  // Waiting queue only).
  const selectQueue = (key) => setState({ queue: key, waiting: "", page: 1 });
  const clearFilters = () => setFilter({ owner: "", product: "", stage: "", priority: "" });
  const activeFilters = [owner, product, stage, priority].filter(Boolean).length;

  const openCase = (caseId) => {
    // return_to is built from this list's OWN current URL, which already
    // holds every filter/sort/page value — so Back restores it exactly.
    navigate(`/process/${caseId}?return_to=${buildReturnTo(location)}`);
  };

  const stageOptions = Array.from(new Set(Object.values(workflows || {}).flat()));
  const cardCount = (card) => dash?.by_queue?.[card.queue];

  const ownerCell = (c) => (canAssign ? (
    <ProcessOwnerPicker caseId={c.case_id} ownerId={c.process_owner_user_id}
      ownerName={c.process_owner_name}
      onAssigned={onOwnerAssigned(c.case_id, {
        process_owner_user_id: c.process_owner_user_id,
        process_owner_name: c.process_owner_name })} />
  ) : (c.process_owner_name || <span className="text-muted-foreground">Unassigned</span>));

  return (
    <div className="space-y-5" data-testid="process-page">
      {/* ------------------------------ HEADER ------------------------------ */}
      <div className="flex items-start gap-3 flex-wrap">
        <div className="min-w-0">
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Process / Cases</h1>
          <p className="text-sm text-muted-foreground">Service delivery after signup.</p>
        </div>
        <IncludeTestToggle className="ml-auto shrink-0" includeTest={includeTest} canInclude={canInclude} onChange={setIncludeTest} testId="process-include-test" />
        {hasCap("crm.create") && (
          <Button variant="outline" data-testid="case-migration-button" className="h-9 shrink-0"
            onClick={() => setMigrationOpen(true)}>
            <span className="hidden sm:inline">Historical migration</span><span className="sm:hidden">Migrate</span>
          </Button>
        )}
        <Button data-testid="case-new-button" className={`${canInclude ? "" : "ml-auto "}h-9 shrink-0`} onClick={() => setNewOpen(true)}>
          <Plus className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">New case</span>
        </Button>
      </div>

      {/* Admin-only; renders nothing unless existing Cases still need
          workflow setup (re-fetched each time this page mounts). */}
      <WorkflowSetupBanner />

      {/* ------------------------- SUMMARY CARDS ---------------------------- */}
      <div className="grid grid-cols-4 xl:grid-cols-7 gap-1.5 sm:gap-3" data-testid="process-dashboard">
        {SUMMARY_CARDS.map((card) => {
          const n = cardCount(card);
          const selected = queue === card.queue;
          const toneCls = n > 0 && card.tone === "danger" ? "text-[hsl(var(--danger))]"
            : n > 0 && card.tone === "warning" ? "text-[hsl(var(--warning))]" : "text-foreground";
          return (
            <button key={card.key} type="button" data-testid={`process-kpi-${card.key}`}
              aria-pressed={selected} onClick={() => selectQueue(card.queue)}
              className={cn(
                "min-w-0 rounded-lg border bg-card px-2 py-1.5 sm:px-3 sm:py-2.5 text-left transition-colors",
                "hover:border-primary/40 hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                selected && "border-primary ring-1 ring-primary bg-primary/[0.04]")}>
              <div className="truncate text-[11px] font-medium text-muted-foreground sm:uppercase sm:tracking-wide">{card.label}</div>
              <div className={cn("sm:mt-0.5 text-xl sm:text-2xl font-semibold tabular-nums leading-tight", toneCls)}
                data-testid={`process-kpi-${card.key}-count`}>
                {dash ? (n ?? 0) : <Skeleton className="h-7 w-10 mt-0.5" />}
              </div>
            </button>
          );
        })}
      </div>
      {dash && (dash.approvals_required > 0 || dash.document_gaps > 0) && (
        <div className="flex gap-2 flex-wrap text-xs -mt-2" data-testid="process-dashboard-alerts">
          {dash.approvals_required > 0 && (
            <Badge variant="outline" className="bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))] border-transparent">
              {dash.approvals_required} case(s) awaiting a signup approval
            </Badge>)}
          {dash.document_gaps > 0 && (
            <Badge variant="outline" className="bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))] border-transparent">
              {dash.document_gaps} case(s) with outstanding document requests
            </Badge>)}
        </div>
      )}

      {/* -------------------------- QUEUE + RESULTS ------------------------- */}
      <Card className="overflow-hidden">
        {/* Main queue tabs — one row, scrolls sideways on narrow screens. */}
        <div role="tablist" aria-label="Case queues" data-testid="case-queue-tabs"
          className="flex gap-1 overflow-x-auto thin-scroll border-b px-2 sm:px-3">
          {QUEUES.map((qq) => {
            const selected = queue === qq.key;
            const n = dash?.by_queue?.[qq.key];
            return (
              <button key={qq.key} type="button" role="tab" aria-selected={selected}
                data-testid={`case-queue-${qq.key}`} data-state={selected ? "active" : "inactive"}
                onClick={() => selectQueue(qq.key)}
                className={cn(
                  "relative shrink-0 whitespace-nowrap px-2.5 sm:px-3 py-2.5 text-sm transition-colors",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm",
                  selected ? "text-foreground font-semibold" : "text-muted-foreground hover:text-foreground")}>
                {qq.label}
                {n != null && (
                  <span className={cn("ml-1.5 rounded-full px-1.5 py-px text-[10px] tabular-nums",
                    selected ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground")}>{n}</span>
                )}
                {selected && <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-primary" />}
              </button>
            );
          })}
        </div>

        {/* Waiting reason — only exists while the Waiting queue is selected. */}
        {isWaiting && (
          <div className="flex items-center gap-2 border-b bg-muted/30 px-3 py-2 overflow-x-auto thin-scroll"
            data-testid="case-waiting-tabs">
            <span className="hidden sm:inline text-xs text-muted-foreground shrink-0">Waiting on</span>
            <div role="tablist" aria-label="Waiting reason" className="flex w-full sm:inline-flex sm:w-auto shrink-0 rounded-md border bg-background p-0.5">
              {WAITING_TABS.map((wt) => {
                const selected = (waiting || "") === wt.key;
                const n = waitingCounts?.[wt.key];
                return (
                  <button key={wt.key || "all"} type="button" role="tab" aria-selected={selected}
                    data-testid={`case-waiting-${wt.key || "all"}`} data-state={selected ? "active" : "inactive"}
                    onClick={() => setFilter({ waiting: wt.key })}
                    className={cn("flex flex-1 flex-col items-center leading-tight sm:flex-none sm:flex-row sm:gap-1.5",
                      "whitespace-nowrap rounded px-1.5 sm:px-2.5 py-1 text-xs transition-colors",
                      selected ? "bg-primary text-primary-foreground font-medium shadow-sm"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted")}>
                    {wt.label}
                    <span className="tabular-nums opacity-80" data-testid={`case-waiting-${wt.key || "all"}-count`}>
                      {n ?? "·"}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Toolbar — four filters + sort in one compact row. On a phone the
            four filters fold behind a "Filters" toggle so the list stays in
            view; sort is always visible. */}
        <div className="flex flex-wrap items-center gap-2 border-b px-3 py-2.5" data-testid="process-filters">
          <Button size="sm" variant="outline" className="h-8 text-xs sm:hidden shrink-0"
            data-testid="case-filter-toggle" aria-expanded={showFilters}
            onClick={() => setShowFilters((s) => !s)}>
            <SlidersHorizontal className="h-3.5 w-3.5 mr-1.5" />Filters
            {activeFilters > 0 && (
              <span className="ml-1.5 rounded-full bg-primary px-1.5 text-[10px] text-primary-foreground tabular-nums">{activeFilters}</span>
            )}
          </Button>
          <div className={cn("order-last basis-full grid grid-cols-2 gap-2",
            "sm:order-none sm:basis-auto sm:flex sm:flex-wrap sm:items-center",
            !showFilters && "hidden")} data-testid="case-filter-group">
            <FilterSelect value={owner} onChange={(v) => setFilter({ owner: v })}
              testId="case-filter-owner" allLabel="All process owners">
              <SelectItem value="unassigned">Unassigned</SelectItem>
              {(users || []).filter((u) => u.is_active !== false).map((u) => (
                <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>))}
            </FilterSelect>
            <FilterSelect value={product} onChange={(v) => setFilter({ product: v })}
              testId="case-filter-product" allLabel="All products">
              {Object.keys(workflows || {}).map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
            </FilterSelect>
            <FilterSelect value={stage} onChange={(v) => setFilter({ stage: v })}
              testId="case-filter-stage" allLabel="All stages">
              {stageOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </FilterSelect>
            <FilterSelect value={priority} onChange={(v) => setFilter({ priority: v })}
              testId="case-filter-priority" allLabel="Any priority">
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="normal">Normal</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </FilterSelect>
            {activeFilters > 0 && (
              <Button size="sm" variant="ghost" className="h-8 text-xs justify-start sm:justify-center" data-testid="case-filter-clear" onClick={clearFilters}>
                <X className="h-3.5 w-3.5 mr-1" />Clear {activeFilters}
              </Button>
            )}
          </div>
          <div className="flex flex-1 items-center gap-1 sm:flex-none sm:ml-auto">
            <Select value={sort} onValueChange={(v) => setFilter({ sort: v })}>
              <SelectTrigger data-testid="case-filter-sort" className="h-8 text-xs w-full sm:w-auto sm:min-w-[172px] justify-start gap-1 bg-background [&>svg]:ml-auto">
                <span className="text-muted-foreground mr-1">Sort:</span><SelectValue />
              </SelectTrigger>
              <SelectContent>{SORTS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
            <Button size="sm" variant="outline" className="h-8 w-8 p-0 shrink-0" data-testid="case-sort-direction"
              aria-label={direction === "asc" ? "Ascending — switch to descending" : "Descending — switch to ascending"}
              title={direction === "asc" ? "Ascending" : "Descending"}
              onClick={() => setFilter({ direction: direction === "asc" ? "desc" : "asc" })}>
              <ArrowDownUp className={cn("h-3.5 w-3.5 transition-transform", direction === "asc" && "rotate-180")} />
            </Button>
          </div>
        </div>

        {/* ------------------------------ RESULTS --------------------------- */}
        {!rows ? (
          <div className="p-4 space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-10" />)}</div>
        ) : rows.length === 0 ? (
          <EmptyState icon={Briefcase} title="No cases match this view"
            hint={activeFilters > 0 ? "Try clearing some filters." : "Create a case for a signed-up customer to start service delivery."}
            testId="cases-empty" />
        ) : (
          <>
            {/* Desktop / tablet: table. */}
            <div className="hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/30 hover:bg-muted/30">
                    <TableHead className="pl-4">Customer / Case</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead>Current stage</TableHead>
                    {/* Process owner and Sales owner are separate columns —
                        never a single ambiguous "Owner". */}
                    <TableHead>Process owner</TableHead>
                    <TableHead className="hidden xl:table-cell">Sales owner</TableHead>
                    <TableHead>Due / Next action</TableHead>
                    <TableHead className="pr-4">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((c) => (
                    <TableRow key={c.case_id} className="cursor-pointer"
                      data-testid={`case-row-${c.case_id}`} onClick={() => openCase(c.case_id)}>
                      <TableCell className="pl-4 py-2.5">
                        <div className="flex items-center gap-1.5">
                          <span className="font-medium">{c.contact_name || "—"}</span>
                          <TestBadge show={c.is_test_record} />
                          <PriorityBadge priority={c.priority} />
                        </div>
                        <div className="font-mono text-[11px] text-muted-foreground">{c.case_id}</div>
                      </TableCell>
                      <TableCell className="text-sm">{c.product_code}</TableCell>
                      <TableCell className="text-sm">{c.current_stage}</TableCell>
                      <TableCell className="text-sm" onClick={(e) => e.stopPropagation()}
                        data-testid={`case-row-owner-${c.case_id}`}>
                        {ownerCell(c)}
                      </TableCell>
                      <TableCell className="hidden xl:table-cell text-sm text-muted-foreground">{c.sales_owner_name || "—"}</TableCell>
                      <TableCell><DueNextAction c={c} /></TableCell>
                      <TableCell className="pr-4"><StatusPill c={c} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Mobile: stacked rows — same data, no sideways scrolling. */}
            <ul className="md:hidden divide-y" data-testid="case-list-mobile">
              {rows.map((c) => (
                <li key={c.case_id} data-testid={`case-card-${c.case_id}`}
                  className="px-3 py-3 active:bg-muted/50 cursor-pointer" onClick={() => openCase(c.case_id)}>
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="font-medium text-sm">{c.contact_name || "—"}</span>
                        <TestBadge show={c.is_test_record} />
                        <PriorityBadge priority={c.priority} />
                      </div>
                      <div className="mt-0.5 text-xs text-muted-foreground">
                        <span className="text-foreground">{c.current_stage}</span> · {c.product_code}
                      </div>
                      <div className="font-mono text-[11px] text-muted-foreground">{c.case_id}</div>
                    </div>
                    <StatusPill c={c} />
                  </div>
                  <div className="mt-2 flex items-end justify-between gap-3">
                    <div className="min-w-0 flex-1"><DueNextAction c={c} compact /></div>
                    <div className="shrink-0 text-xs" onClick={(e) => e.stopPropagation()}>
                      {ownerCell(c)}
                    </div>
                  </div>
                </li>
              ))}
            </ul>

            {/* Real pagination against an EXACT server-side total. */}
            <div className="flex items-center justify-between gap-2 px-3 py-2.5 border-t text-xs" data-testid="case-pagination">
              <span className="text-muted-foreground" data-testid="case-pagination-summary">
                <span className="hidden sm:inline">Showing </span>{(page - 1) * page_size + 1}–{Math.min(page * page_size, total)} of <b className="text-foreground">{total}</b>
                <span className="hidden sm:inline"> case{total === 1 ? "" : "s"}</span>
              </span>
              <div className="flex items-center gap-1">
                <Select value={String(page_size)} onValueChange={(v) => setState({ page_size: Number(v), page: 1 })}>
                  <SelectTrigger data-testid="case-page-size" className="h-7 w-[92px] text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{[10, 25, 50, 100].map((n) => <SelectItem key={n} value={String(n)}>{n}/page</SelectItem>)}</SelectContent>
                </Select>
                <Button size="sm" variant="outline" className="h-7 px-2" data-testid="case-page-prev"
                  aria-label="Previous page" disabled={page <= 1} onClick={() => setState({ page: page - 1 })}>
                  <ChevronLeft className="h-3.5 w-3.5" />
                </Button>
                <span className="px-1 tabular-nums" data-testid="case-page-indicator">{page} / {pages}</span>
                <Button size="sm" variant="outline" className="h-7 px-2" data-testid="case-page-next"
                  aria-label="Next page" disabled={page >= pages} onClick={() => setState({ page: page + 1 })}>
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>
      <NewCaseDialog open={newOpen} setOpen={setNewOpen} onCreated={load} includeTest={includeTest} />
      <HistoricalMigrationDialog open={migrationOpen} setOpen={setMigrationOpen} onDone={load} />
    </div>
  );
}
